/**
 * The prescription builder (§7, Phase 3).
 *
 * THIS FILE CONTAINS NO EXERCISE-SPECIFIC CODE, and that is the point (§1).
 * It asks the registry what exists, renders each exercise's settings from its
 * own `ParamSpec` declarations, asks the exercise how long it will take, and
 * asks it whether the values make clinical sense. It has never heard of smooth
 * pursuit. There is no switch on exercise id here and there must never be one —
 * if one seems necessary, the contract in packages/exercises/src/types.ts is
 * missing something.
 *
 * Two things carried forward from Phase 2, both logged in DECISIONS.md:
 *
 *   Steppers, not sliders. A clinical dose is set by intent, not by hand
 *   position, and two prescriptions meant to be identical must not end up a
 *   degree apart.
 *
 *   Validation has a severity. A blocking message means the exercise would not
 *   measure what it claims and Save is refused. An advisory means the settings
 *   are unusual but legitimate — it is shown and then got out of the way. There
 *   is no "yes I meant it" checkbox; that would be friction charged against the
 *   doctor's own judgment (constraint 4).
 *
 * The schedule — the order sessions come in and how many a day — is edited in
 * SchedulePanel and saved with the draft. A published
 * prescription without one alternates by completion, which is the schema
 * default.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  available,
  getDefinition,
  hasBlocking,
  INPUT_MODE_LABELS,
  INPUT_MODE_PLACEHOLDER,
  POSITION_LABELS,
  takesNoInput,
  validateParams,
  withDefaults,
  type ExerciseDefinition,
  type ValidationMessage,
} from '@pathways/exercises';
import {
  api,
  ApiError,
  type InputModeValue,
  type PositionValue,
  type PrescribedExercise,
  type PrescriptionResponse,
  type ProgramSessionDraft,
  type SafetySettings,
  type ScheduleDraft,
} from '../../api.js';
import { ParamForm } from '../../components/ParamForm.js';
import { PathPreview } from '../../components/PathPreview.js';
import { PreviewPanel } from '../../components/PreviewPanel.js';
import { summariseChanges, type SessionChanges } from './changes.js';
import { formatTime, sessionTime, SOFT_WARN_SECONDS } from './timing.js';
import { SchedulePanel } from './SchedulePanel.js';
import { SafetyPanel, type SafetySaveState } from './SafetyPanel.js';
import { SafetyEvents } from './SafetyEvents.js';
import styles from './PrescriptionBuilder.module.css';

const SESSION_LABELS = 'ABCDEFGH'.split('');

/**
 * How long the builder waits after the last keystroke before writing the draft.
 *
 * The draft is invisible to the patient — publishing is the deliberate act — so
 * writing it continuously costs nothing clinically and removes the only way to
 * lose an afternoon's work: navigating away from a screen that merely said
 * "unsaved changes" and let you leave. Long enough not to write on every stroke
 * of a stepper, short enough that the exposure is seconds.
 */
const AUTOSAVE_DELAY_MS = 1500;

const cardKey = (label: string, index: number) => `${label}#${index}`;

/**
 * Keep the open/closed state attached to the right card when the list around it
 * changes. The key carries a position, so every structural edit has to move it —
 * otherwise removing the first exercise leaves the second one wearing the
 * first's state.
 */
function remapExpanded(
  open: ReadonlySet<string>,
  label: string,
  move: (index: number) => number | null,
): ReadonlySet<string> {
  const next = new Set<string>();
  for (const key of open) {
    const hash = key.lastIndexOf('#');
    const owner = key.slice(0, hash);
    if (owner !== label) {
      next.add(key);
      continue;
    }
    const moved = move(Number(key.slice(hash + 1)));
    if (moved !== null) next.add(cardKey(label, moved));
  }
  return next;
}

/** What was last taken away, and how to put it back. */
type Undoable =
  | { kind: 'exercise'; label: string; index: number; exercise: PrescribedExercise }
  | { kind: 'session'; index: number; session: ProgramSessionDraft };

export function PrescriptionBuilder() {
  const { patientId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [sessions, setSessions] = useState<ProgramSessionDraft[] | null>(null);
  /** How the program runs: order, how many a day, how far apart. */
  const [schedule, setSchedule] = useState<ScheduleDraft | null>(null);
  /**
   * The patient's safety settings (§7). Held and saved SEPARATELY from the
   * draft: they are not published, and they take effect the moment they are
   * written. See SafetyPanel.
   */
  const [safety, setSafety] = useState<SafetySettings | null>(null);
  const [safetyDirty, setSafetyDirty] = useState(false);
  const [safetyFailed, setSafetyFailed] = useState(false);
  const [activeLabel, setActiveLabel] = useState<string>('A');
  const [dirty, setDirty] = useState(false);
  const [publishNote, setPublishNote] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Index within the active session of the exercise being watched, if any. */
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [undoable, setUndoable] = useState<Undoable | null>(null);
  /**
   * Which exercise cards are open, as `label#index`.
   *
   * Collapsed is the resting state: opening a patient is usually to review the
   * shape of a program or change one thing, and a session of six exercises is
   * several screens of settings to scroll past otherwise. A card opens when it
   * is added, because that is when it is about to be configured.
   */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const loaded = useQuery({
    queryKey: ['prescription', patientId],
    queryFn: () => api.get<PrescriptionResponse>(`/patients/${patientId}/prescription`),
    retry: false,
  });

  // Seed the editor once. A patient with a published program but no draft starts
  // from a copy of what they are currently doing — which only becomes a draft
  // row on first save, so merely opening a program never writes anything.
  useEffect(() => {
    if (!loaded.data || sessions !== null) return;
    const source = loaded.data.draft ?? loaded.data.published;
    const seeded: ProgramSessionDraft[] =
      source && source.sessions.length > 0
        ? source.sessions.map((s) => ({
            label: s.label,
            // Drop row ids when copying a PUBLISHED program: those rows belong
            // to the published version and must not be edited in place.
            exercises: s.exercises.map((e) =>
              loaded.data.draft ? e : { ...e, id: undefined },
            ),
          }))
        : [{ label: 'A', exercises: [] }];
    setSessions(seeded);
    setActiveLabel(seeded[0]?.label ?? 'A');
    setSafety(loaded.data.patient.safety);
    // The server always sends a schedule, defaulted rather than null, so there
    // is never a missing-schedule case for this screen to interpret.
    setSchedule(
      source?.schedule ?? {
        rotation: seeded.map((session) => session.label),
        aimPerDay: 1,
        maxPerDay: 1,
        minGapHours: 2,
      },
    );
  }, [loaded.data, sessions]);

  const saveDraft = useMutation({
    mutationFn: (next: { sessions: ProgramSessionDraft[]; schedule: ScheduleDraft | null }) =>
      api.put<{ draft: { sessions: ProgramSessionDraft[]; schedule: ScheduleDraft } }>(
        `/patients/${patientId}/prescription/draft`,
        { sessions: next.sessions, schedule: next.schedule ?? undefined },
      ),
    onSuccess: (result) => {
      // Adopt the server's copy so newly created rows pick up their ids, and so
      // a rotation naming a removed session comes back reconciled.
      setSessions(result.draft.sessions);
      setSchedule(result.draft.schedule);
      setDirty(false);
      setSaveError(null);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      void queryClient.invalidateQueries({ queryKey: ['prescription', patientId] });
    },
    onError: () => setSaveError('Could not save. Nothing was changed on the server.'),
  });

  /**
   * Safety settings save on their own, not with the draft.
   *
   * A failed write leaves `safetyDirty` true so the panel can say the previous
   * settings are still in force. That wording is deliberate: a limit the doctor
   * believes he has tightened and has not is the one failure here that could
   * hurt somebody, so it is never reported as merely "not saved".
   */
  const saveSafety = useMutation({
    mutationFn: (next: SafetySettings) =>
      api.patch<{ safety: SafetySettings }>(`/patients/${patientId}/safety`, next),
    onSuccess: (result) => {
      // Adopt the server's copy, which has been clamped to the allowed range.
      setSafety(result.safety);
      setSafetyDirty(false);
      setSafetyFailed(false);
      void queryClient.invalidateQueries({ queryKey: ['prescription', patientId] });
    },
    onError: () => setSafetyFailed(true),
  });

  useEffect(() => {
    if (!safetyDirty || safety === null) return;
    // The runner could never honour an exercise cap above the session cap, so
    // the write waits for one of the two to move rather than storing it.
    if (safety.maxExerciseMinutes > safety.maxSessionMinutes) return;
    const timer = setTimeout(() => saveSafety.mutate(safety), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // saveSafety is recreated every render; see the note on the draft autosave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safetyDirty, safety]);

  const publish = useMutation({
    mutationFn: (note: string) =>
      api.post(`/patients/${patientId}/prescription/publish`, { note }),
    onSuccess: () => {
      setPublishing(false);
      setPublishNote('');
      setSessions(null);
      void queryClient.invalidateQueries({ queryKey: ['patients'] });
      void queryClient.invalidateQueries({ queryKey: ['prescription', patientId] });
    },
    onError: () => setSaveError('Could not publish. The draft is untouched.'),
  });

  const registry = useMemo(() => available(), []);

  // Validate everything, everywhere, on every keystroke. validateParams is pure
  // and the exercises' estimates are required to be fast, so this is cheap.
  const validation = useMemo(() => {
    const byKey = new Map<string, ValidationMessage[]>();
    let blocked = false;
    for (const session of sessions ?? []) {
      session.exercises.forEach((exercise, index) => {
        const definition = getDefinition(exercise.exerciseId);
        const messages = definition ? validateParams(definition, exercise.params) : [];
        byKey.set(`${session.label}/${index}`, messages);
        if (hasBlocking(messages)) blocked = true;
      });
    }
    return { byKey, blocked };
  }, [sessions]);

  // Write the draft shortly after the doctor stops typing. Held while a
  // blocking message stands, because the server refuses those anyway.
  const blocked = validation.blocked;
  useEffect(() => {
    if (!dirty || blocked || sessions === null || publish.isPending) return;
    // The ceiling sitting below the aim is the schedule's one blocking state:
    // it would prescribe a dose the app then refuses to deliver.
    if (schedule && schedule.maxPerDay < schedule.aimPerDay) return;
    const timer = setTimeout(() => saveDraft.mutate({ sessions, schedule }), AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
    // saveDraft and publish are recreated every render; depending on them here
    // would restart the timer continuously and nothing would ever be written.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, blocked, sessions, schedule]);

  // Covers the only gap autosave leaves: closing or reloading the tab inside
  // the delay, or while a blocking message is holding the write. Safety
  // settings count too — leaving with an unwritten one is how a limit the
  // doctor believes is in force turns out not to be.
  useEffect(() => {
    if (!dirty && !safetyDirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, safetyDirty]);

  if (loaded.isError) {
    if (loaded.error instanceof ApiError && loaded.error.status === 401) {
      navigate('/clinician/login', { replace: true });
      return null;
    }
    return <p className={styles.state}>Could not load this patient.</p>;
  }

  if (loaded.isPending || sessions === null) {
    return <p className={styles.state}>Loading…</p>;
  }

  const patient = loaded.data.patient;
  const published = loaded.data.published;
  const active = sessions.find((s) => s.label === activeLabel) ?? sessions[0];

  // Never empty by construction — removeSession refuses to drop the last one.
  if (!active) return <p className={styles.state}>Loading…</p>;

  const mutate = (next: ProgramSessionDraft[]) => {
    setSessions(next);
    setDirty(true);
    setSaveError(null);
    // Any other edit invalidates the offer: the position it would restore to
    // may no longer be the position it came from.
    setUndoable(null);
  };

  const updateActive = (fn: (session: ProgramSessionDraft) => ProgramSessionDraft) =>
    mutate(sessions.map((s) => (s.label === active.label ? fn(s) : s)));

  const addExercise = (definition: ExerciseDefinition) => {
    setExpanded((open) => new Set(open).add(cardKey(active.label, active.exercises.length)));
    updateActive((session) => ({
      ...session,
      exercises: [
        ...session.exercises,
        {
          exerciseId: definition.id,
          params: withDefaults(definition),
          position: definition.allowedPositions[0] as PositionValue,
          inputMode: takesNoInput(definition)
            ? INPUT_MODE_PLACEHOLDER
            : (definition.allowedInputModes[0] as InputModeValue),
          patientNote: null,
        },
      ],
    }));
  };

  const changeExercise = (index: number, patch: Partial<PrescribedExercise>) =>
    updateActive((session) => ({
      ...session,
      exercises: session.exercises.map((e, i) => (i === index ? { ...e, ...patch } : e)),
    }));

  const removeExercise = (index: number) => {
    const removed = active.exercises[index];
    updateActive((session) => ({
      ...session,
      exercises: session.exercises.filter((_, i) => i !== index),
    }));
    if (previewIndex !== null) setPreviewIndex(null);
    setExpanded((open) =>
      remapExpanded(open, active.label, (i) =>
        i === index ? null : i > index ? i - 1 : i,
      ),
    );
    if (removed) {
      setUndoable({ kind: 'exercise', label: active.label, index, exercise: removed });
    }
  };

  const moveExercise = (index: number, delta: number) => {
    const target = index + delta;
    setExpanded((open) =>
      remapExpanded(open, active.label, (i) => {
        if (i === index) return target;
        if (index < target && i > index && i <= target) return i - 1;
        if (index > target && i >= target && i < index) return i + 1;
        return i;
      }),
    );
    updateActive((session) => {
      const next = [...session.exercises];
      const target = index + delta;
      if (target < 0 || target >= next.length) return session;
      const [row] = next.splice(index, 1);
      if (row) next.splice(target, 0, row);
      return { ...session, exercises: next };
    });
  };

  const addSession = () => {
    const used = new Set(sessions.map((s) => s.label));
    const label = SESSION_LABELS.find((l) => !used.has(l));
    if (!label) return;
    // Inserted in label order, not appended. Removing B and adding another
    // otherwise gave tabs reading A, C, B.
    const next = [...sessions, { label, exercises: [] }].sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    mutate(next);
    setActiveLabel(label);
  };

  /**
   * Copy the session on screen into the next free label. Building B as "A with
   * two changes" was otherwise a full rebuild, which quietly pushes a program
   * toward being simpler than it was meant to be.
   */
  const duplicateSession = () => {
    const used = new Set(sessions.map((s) => s.label));
    const label = SESSION_LABELS.find((l) => !used.has(l));
    if (!label) return;
    const copy: ProgramSessionDraft = {
      label,
      // Row ids belong to the rows they were saved as. A copy is a new row.
      exercises: active.exercises.map((e) => ({
        ...e,
        id: undefined,
        params: { ...e.params },
      })),
    };
    mutate(
      [...sessions, copy].sort((a, b) => a.label.localeCompare(b.label)),
    );
    setActiveLabel(label);
  };

  const removeSession = (label: string) => {
    if (sessions.length <= 1) return;
    const index = sessions.findIndex((s) => s.label === label);
    const removed = sessions[index];
    const next = sessions.filter((s) => s.label !== label);
    mutate(next);
    if (activeLabel === label) setActiveLabel(next[0]?.label ?? 'A');
    if (removed) setUndoable({ kind: 'session', index, session: removed });
  };

  /**
   * Put back exactly what was taken, where it was taken from. Not a general
   * history — one step, offered until the next edit, which covers the mistake
   * this is for: the wrong button, noticed immediately.
   */
  const undoRemoval = () => {
    if (!undoable) return;
    if (undoable.kind === 'session') {
      const next = [...sessions];
      next.splice(undoable.index, 0, undoable.session);
      mutate(next);
      setActiveLabel(undoable.session.label);
      return;
    }
    setExpanded((open) =>
      remapExpanded(open, undoable.label, (i) =>
        i >= undoable.index ? i + 1 : i,
      ),
    );
    mutate(
      sessions.map((s) => {
        if (s.label !== undoable.label) return s;
        const exercises = [...s.exercises];
        exercises.splice(undoable.index, 0, undoable.exercise);
        return { ...s, exercises };
      }),
    );
    setActiveLabel(undoable.label);
  };

  const isEmpty = sessions.every((s) => s.exercises.length === 0);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <Link className={styles.backLink} to="/clinician/patients">
            ← Patients
          </Link>
          <h1 className={styles.heading}>{patient.code}</h1>
          <p className={styles.status}>
            {published
              ? `Currently running version ${published.version}`
              : 'No published program yet'}
            <SaveState
              dirty={dirty}
              saving={saveDraft.isPending}
              held={validation.blocked}
            />
          </p>
        </div>

        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.primaryButton}
            disabled={validation.blocked || isEmpty}
            onClick={() => setPublishing(true)}
          >
            Publish…
          </button>
        </div>
      </header>

      {saveError && (
        <p className={styles.errorBanner}>
          {saveError}{' '}
          <button
            type="button"
            className={styles.inlineButton}
            onClick={() => saveDraft.mutate({ sessions, schedule })}
          >
            Try again
          </button>
        </p>
      )}

      {undoable && (
        <p className={styles.undoBar}>
          {undoable.kind === 'session'
            ? `Removed session ${undoable.session.label}.`
            : `Removed ${
                getDefinition(undoable.exercise.exerciseId)?.name ?? 'an exercise'
              } from session ${undoable.label}.`}{' '}
          <button type="button" className={styles.inlineButton} onClick={undoRemoval}>
            Undo
          </button>
        </p>
      )}
      {validation.blocked && (
        <p className={styles.errorBanner}>
          Some settings below would stop an exercise measuring what it is meant to
          measure. They are marked in red. Saving is held until those are resolved
          — everything else is yours to decide.
        </p>
      )}

      <nav className={styles.sessionTabs} aria-label="Program sessions">
        {sessions.map((session) => (
          <button
            key={session.label}
            type="button"
            className={session.label === active.label ? styles.tabActive : styles.tab}
            aria-current={session.label === active.label}
            onClick={() => {
              setActiveLabel(session.label);
              setPreviewIndex(null);
            }}
          >
            Session {session.label}
            <span className={styles.tabCount}>
              {session.exercises.length === 0
                ? 'empty'
                : formatTime(sessionTime(session).therapy)}
            </span>
          </button>
        ))}
        {sessions.length < SESSION_LABELS.length && (
          <button type="button" className={styles.addTab} onClick={addSession}>
            + Add session
          </button>
        )}
      </nav>

      <SchedulePanel
        schedule={
          schedule ?? {
            rotation: sessions.map((session) => session.label),
            aimPerDay: 1,
            maxPerDay: 1,
            minGapHours: 2,
          }
        }
        labels={sessions.map((session) => session.label)}
        onChange={(next) => {
          setSchedule(next);
          setDirty(true);
        }}
      />

      {/*
        Above the settings on purpose. What has already happened to this patient
        is the context for what the settings should be, and a doctor opening the
        chart after a session went wrong is looking for this first.
      */}
      <SafetyEvents patientId={patientId} />

      {safety && (
        <SafetyPanel
          settings={safety}
          state={safetyState(safetyDirty, safetyFailed, saveSafety.isPending)}
          onChange={(next) => {
            setSafety(next);
            setSafetyDirty(true);
            setSafetyFailed(false);
          }}
          onRetry={() => saveSafety.mutate(safety)}
        />
      )}

      <SessionTiming session={active} />

      {active.exercises.length > 1 && (
        <div className={styles.listActions}>
          <button
            type="button"
            className={styles.sessionAction}
            onClick={() =>
              setExpanded((open) =>
                remapExpanded(
                  new Set([
                    ...open,
                    ...active.exercises.map((_, i) => cardKey(active.label, i)),
                  ]),
                  active.label,
                  (i) => i,
                ),
              )
            }
          >
            Expand all
          </button>
          <button
            type="button"
            className={styles.sessionAction}
            onClick={() => setExpanded((open) => remapExpanded(open, active.label, () => null))}
          >
            Collapse all
          </button>
        </div>
      )}

      <div className={styles.exerciseList}>
        {active.exercises.length === 0 && (
          <p className={styles.state}>
            Session {active.label} is empty. Add an exercise below.
          </p>
        )}

        {active.exercises.map((exercise, index) => {
          const definition = getDefinition(exercise.exerciseId);
          if (!definition) {
            return (
              <p key={`${exercise.exerciseId}-${index}`} className={styles.errorBanner}>
                This program references an exercise that is no longer registered
                ({exercise.exerciseId}). Its past results are unaffected. Remove it
                here and choose a replacement.
              </p>
            );
          }
          return (
            <ExerciseCard
              key={exercise.id ?? `new-${index}`}
              definition={definition}
              exercise={exercise}
              index={index}
              count={active.exercises.length}
              messages={validation.byKey.get(`${active.label}/${index}`) ?? []}
              onChange={(patch) => changeExercise(index, patch)}
              onRemove={() => removeExercise(index)}
              onMove={(delta) => moveExercise(index, delta)}
              onPreview={() => setPreviewIndex(index)}
              previewOpen={previewIndex === index}
              onClosePreview={() => setPreviewIndex(null)}
              open={expanded.has(cardKey(active.label, index))}
              onToggleOpen={(next) => {
                if (!next && previewIndex === index) setPreviewIndex(null);
                setExpanded((prev) => {
                  const copy = new Set(prev);
                  const key = cardKey(active.label, index);
                  if (next) copy.add(key);
                  else copy.delete(key);
                  return copy;
                });
              }}
            />
          );
        })}
      </div>

      <AddExercise registry={registry} onAdd={addExercise} />

      <div className={styles.sessionActions}>
        <button
          type="button"
          className={styles.sessionAction}
          disabled={sessions.length >= SESSION_LABELS.length}
          onClick={duplicateSession}
        >
          Duplicate session {active.label}
        </button>
        {sessions.length > 1 && (
          <button
            type="button"
            className={styles.sessionAction}
            onClick={() => removeSession(active.label)}
          >
            Remove session {active.label}
          </button>
        )}
      </div>

      {publishing && (
        <PublishDialog
          version={(published?.version ?? 0) + 1}
          changes={summariseChanges(published?.sessions ?? null, sessions)}
          replacing={published?.version ?? null}
          note={publishNote}
          busy={publish.isPending}
          dirty={dirty}
          onNote={setPublishNote}
          onCancel={() => setPublishing(false)}
          onConfirm={async () => {
            if (dirty) await saveDraft.mutateAsync({ sessions, schedule });
            publish.mutate(publishNote.trim());
          }}
        />
      )}
    </div>
  );
}

/**
 * Where the draft stands, in the place the "unsaved changes" flag used to sit.
 *
 * It says "saved" rather than offering a button because there is nothing left
 * to press: the draft writes itself. The one state worth naming is the held
 * one — a blocking message stops the write, and silence there would look like
 * saving while nothing was being saved.
 */
/**
 * Which of the four words the safety panel shows. Saving wins over failed:
 * pressing "try again" has to look like something happened.
 */
function safetyState(dirty: boolean, failed: boolean, saving: boolean): SafetySaveState {
  if (saving) return 'saving';
  if (failed) return 'error';
  if (dirty) return 'editing';
  return 'saved';
}

function SaveState({
  dirty,
  saving,
  held,
}: {
  dirty: boolean;
  saving: boolean;
  held: boolean;
}) {
  if (saving) return <span className={styles.saveState}>saving…</span>;
  if (dirty && held) {
    return <span className={styles.dirtyFlag}>not saved — see the red notes below</span>;
  }
  if (dirty) return <span className={styles.saveState}>editing…</span>;
  return <span className={styles.saveState}>draft saved</span>;
}

// =============================================================================
// Running time (§7) — the arithmetic is in timing.ts, and tested there
// =============================================================================

/**
 * The always-visible running total (§7). Shows therapy time and the realistic
 * session length side by side, because the gap between them IS the friction
 * budget — a 10-minute prescription that lands at 12 with transitions is the
 * intended shape, and one that lands at 18 is not.
 */
function SessionTiming({ session }: { session: ProgramSessionDraft }) {
  const { therapy, realistic, counted } = sessionTime(session);
  const over = therapy > SOFT_WARN_SECONDS;

  return (
    <div className={over ? styles.timingWarn : styles.timing}>
      <div className={styles.timingFigure}>
        <span className={styles.timingValue}>{formatTime(therapy)}</span>
        <span className={styles.timingLabel}>therapy</span>
      </div>
      <div className={styles.timingFigure}>
        <span className={styles.timingValue}>{formatTime(realistic)}</span>
        <span className={styles.timingLabel}>with transitions</span>
      </div>
      {/* Third figure rather than a note, because it belongs to the arithmetic
          of the second one: every exercise adds a transition, and the gap
          between the two times is this count plus one close-out. */}
      <div className={styles.timingFigure}>
        <span className={styles.timingValue}>{counted}</span>
        <span className={styles.timingLabel}>total exercises</span>
      </div>
      {over && (
        <p className={styles.timingNote}>
          Longer than the usual twelve-minute target.
        </p>
      )}
    </div>
  );
}

// =============================================================================
// One prescribed exercise
// =============================================================================

function ExerciseCard({
  definition,
  exercise,
  index,
  count,
  messages,
  onChange,
  onRemove,
  onMove,
  onPreview,
  previewOpen,
  onClosePreview,
  open,
  onToggleOpen,
}: {
  definition: ExerciseDefinition;
  exercise: PrescribedExercise;
  index: number;
  count: number;
  messages: readonly ValidationMessage[];
  onChange: (patch: Partial<PrescribedExercise>) => void;
  onRemove: () => void;
  onMove: (delta: number) => void;
  onPreview: () => void;
  previewOpen: boolean;
  onClosePreview: () => void;
  open: boolean;
  onToggleOpen: (next: boolean) => void;
}) {
  const idPrefix = `ex-${index}-${definition.id}`;
  const crossField = messages.filter((m) => !m.param);

  // A collapsed card must never hide the reason saving is refused. Blocking
  // messages hold the save, and a screen that will not save with nothing on it
  // to explain why is worse than a long scroll.
  const held = hasBlocking(messages);
  const isOpen = open || held;

  // §9 calls a non-seated position combined with head movement or eyes closed
  // the highest-risk combination in the library, and constraint 14 requires a
  // soft confirmation. This is that confirmation: stated, not clicked through.
  const riskyStance =
    exercise.position === 'tandem' &&
    (definition.involvesHeadMovement === true || definition.involvesEyesClosed === true);

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.cardIdentity}>
          <button
            type="button"
            className={styles.disclosure}
            aria-expanded={isOpen}
            aria-controls={`${idPrefix}-body`}
            disabled={held}
            title={held ? 'Held open until the settings in red are resolved' : undefined}
            onClick={() => onToggleOpen(!isOpen)}
          >
            <span aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
            <span className={styles.visuallyHidden}>
              {isOpen ? 'Collapse' : 'Expand'} {definition.name}
            </span>
          </button>
          <div>
            <h2 className={styles.cardTitle}>{definition.name}</h2>
            {isOpen ? (
              <p className={styles.cardDescription}>{definition.clinicalDescription}</p>
            ) : (
              /* Enough to read the session without opening anything, and
                 nothing exercise-specific — these three are declared by every
                 exercise, so a new one summarises correctly on the day it is
                 registered. */
              <p className={styles.cardSummary}>
                {POSITION_LABELS[exercise.position]} · responds with{' '}
                {INPUT_MODE_LABELS[exercise.inputMode].toLowerCase()}
              </p>
            )}
          </div>
        </div>
        <div className={styles.cardControls}>
          <span className={styles.cardDuration}>
            {formatTime(definition.estimateDurationSec(exercise.params))}
          </span>
          {/* A dose is a visual judgement. Watching it at these exact settings
              is the only way to make that judgement honestly. */}
          {isOpen && (
            <button
              type="button"
              className={styles.previewButton}
              aria-pressed={previewOpen}
              onClick={previewOpen ? onClosePreview : onPreview}
            >
              {previewOpen ? 'Hide preview' : 'Preview'}
            </button>
          )}
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Move earlier"
            disabled={index === 0}
            onClick={() => onMove(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className={styles.iconButton}
            aria-label="Move later"
            disabled={index === count - 1}
            onClick={() => onMove(1)}
          >
            ↓
          </button>
          <button type="button" className={styles.removeButton} onClick={onRemove}>
            Remove
          </button>
        </div>
      </header>

      {/* Settings on the left, the running preview on the right. Judging a dose
          means changing a number and seeing what it did, so the two have to be
          visible at the same moment. Without a preview open the settings take
          the whole width. */}
      {isOpen && (
      <>
      <div
        id={`${idPrefix}-body`}
        className={previewOpen ? styles.cardBodySplit : styles.cardBody}
      >
        <div className={styles.cardSettings}>
      <div className={styles.deliveryRow}>
        <label className={styles.deliveryField} htmlFor={`${idPrefix}-position`}>
          <span className={styles.deliveryLabel}>Position</span>
          <select
            id={`${idPrefix}-position`}
            className={styles.select}
            value={exercise.position}
            onChange={(e) => onChange({ position: e.target.value as PositionValue })}
          >
            {definition.allowedPositions.map((position) => (
              <option key={position} value={position}>
                {POSITION_LABELS[position]}
              </option>
            ))}
          </select>
        </label>

        {/*
          * An exercise that takes no input offers no choice here. A control
          * that changes nothing is worse than an absent one — see the
          * `allowedInputModes` contract note.
          */}
        {!takesNoInput(definition) && (
          <label className={styles.deliveryField} htmlFor={`${idPrefix}-input`}>
            <span className={styles.deliveryLabel}>Responds with</span>
            <select
              id={`${idPrefix}-input`}
              className={styles.select}
              value={exercise.inputMode}
              onChange={(e) => onChange({ inputMode: e.target.value as InputModeValue })}
            >
              {definition.allowedInputModes.map((mode) => (
                <option key={mode} value={mode}>
                  {INPUT_MODE_LABELS[mode]}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {exercise.position !== 'seated' && (
        <p className={styles.stanceNote}>
          Standing work needs a wall or counter within reach, and never an unstable
          surface. If this patient stops mid-exercise they will be told to sit down
          first.
        </p>
      )}

      {riskyStance && (
        <p className={styles.stanceWarn}>
          Tandem stance combined with head movement or eyes closed is the highest
          fall risk this library can produce. Prescribe it only for a patient you
          have assessed standing.
        </p>
      )}

      <ParamForm
        params={definition.params}
        values={exercise.params}
        idPrefix={idPrefix}
        messages={messages}
        onChange={(key, value) =>
          onChange({ params: { ...exercise.params, [key]: value } })
        }
      />

      <PathPreview definition={definition} params={exercise.params} />

      {crossField.length > 0 && (
        <ul className={styles.crossFieldNotes}>
          {crossField.map((m) => (
            <li
              key={m.message}
              className={m.severity === 'blocking' ? styles.blocking : styles.advisory}
            >
              <strong>{m.severity === 'blocking' ? 'Will not run:' : 'Note:'}</strong>{' '}
              {m.message}
            </li>
          ))}
        </ul>
      )}
        </div>

        {previewOpen && (
          <PreviewPanel
            definition={definition}
            params={exercise.params}
            position={exercise.position}
            inputMode={exercise.inputMode}
            onClose={onClosePreview}
          />
        )}
      </div>

      <label className={styles.noteField} htmlFor={`${idPrefix}-note`}>
        <span className={styles.deliveryLabel}>Note to the patient (optional)</span>
        <input
          id={`${idPrefix}-note`}
          className={styles.noteInput}
          maxLength={200}
          value={exercise.patientNote ?? ''}
          placeholder="e.g. Take the full rest between rounds"
          onChange={(e) => onChange({ patientNote: e.target.value })}
        />
        <span className={styles.noteHelp}>
          The patient reads this. Keep it to how to do the exercise — no clinical
          detail, and nothing that would identify them.
        </span>
      </label>
      </>
      )}
    </section>
  );
}

// =============================================================================
// Adding an exercise — straight from the registry, grouped as it declares
// =============================================================================

function AddExercise({
  registry,
  onAdd,
}: {
  registry: ExerciseDefinition[];
  onAdd: (definition: ExerciseDefinition) => void;
}) {
  const [choice, setChoice] = useState(registry[0]?.id ?? '');
  const selected = registry.find((d) => d.id === choice);

  if (registry.length === 0) {
    return <p className={styles.state}>No exercises are registered.</p>;
  }

  return (
    <div className={styles.addExercise}>
      <label className={styles.deliveryLabel} htmlFor="add-exercise">
        Add an exercise
      </label>
      <div className={styles.addExerciseRow}>
        <select
          id="add-exercise"
          className={styles.select}
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
        >
          {registry.map((definition) => (
            <option key={definition.id} value={definition.id}>
              {definition.name} — {definition.category}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={styles.secondaryButton}
          disabled={!selected}
          onClick={() => selected && onAdd(selected)}
        >
          Add
        </button>
      </div>
      {selected && <p className={styles.addExerciseHelp}>{selected.clinicalDescription}</p>}
    </div>
  );
}

// =============================================================================
// Publishing
// =============================================================================

function PublishDialog({
  version,
  changes,
  replacing,
  note,
  busy,
  dirty,
  onNote,
  onCancel,
  onConfirm,
}: {
  version: number;
  changes: SessionChanges[];
  /** The version this replaces, or null on a first publish. */
  replacing: number | null;
  note: string;
  busy: boolean;
  dirty: boolean;
  onNote: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className={styles.dialogBackdrop} role="dialog" aria-modal="true" aria-labelledby="publish-heading">
      <div className={styles.dialog}>
        <h2 className={styles.dialogHeading} id="publish-heading">
          Publish version {version}
        </h2>
        <p className={styles.dialogBody}>
          This becomes what the patient does from their next session. The version
          they were on is kept exactly as it was, and the note below is dropped as a
          marker on their progress charts so a change in the numbers later can be
          read against a change in the program.
          {dirty && ' Unsaved edits will be saved first.'}
        </p>

        {/* What is actually changing, rather than a version number and a
            request to remember. Publishing alters what a real patient does
            tomorrow, and it may be days since the draft was started. */}
        {replacing !== null && changes.length === 0 ? (
          <p className={styles.dialogNothing}>
            Nothing has changed since version {replacing}. Publishing now would add
            a version to this patient's history that reads the same as the one
            they are already doing.
          </p>
        ) : (
          <div className={styles.changeList}>
            <h3 className={styles.changeHeading}>
              {replacing === null
                ? 'This program'
                : `What changes from version ${replacing}`}
            </h3>
            {changes.map((session) => (
              <div key={session.label} className={styles.changeSession}>
                <h4 className={styles.changeSessionLabel}>Session {session.label}</h4>
                <ul className={styles.changeLines}>
                  {session.lines.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        <label className={styles.deliveryLabel} htmlFor="publish-note">
          What changed, and why
        </label>
        <input
          id="publish-note"
          className={styles.noteInput}
          maxLength={200}
          value={note}
          placeholder="e.g. re-exam 3/14 — shifting focus to vestibular"
          onChange={(e) => onNote(e.target.value)}
        />
        <div className={styles.dialogActions}>
          <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={styles.primaryButton} onClick={onConfirm} disabled={busy}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
}
